import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, ZoomIn } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import ContactSection from '../components/ContactSection';
import GalleryLightboxModal from '../components/GalleryLightboxModal';
import { FALLBACK_PROJECTS, SITE_INFO } from '../data';
import { fetchCollection } from '../lib/cms';

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  
  // Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImgIndex, setActiveImgIndex] = useState(0);

  useEffect(() => {
    async function loadProject() {
      const cmsProjects = await fetchCollection('projects');
      const allProjects = (cmsProjects && cmsProjects.length > 0) ? cmsProjects : FALLBACK_PROJECTS;
      const found = allProjects.find(
        (p) =>
          p.url_slug === slug ||
          p.slug === slug ||
          (typeof p.slug === 'object' && p.slug?._ === slug)
      );
      setProject(found || allProjects[0]);
    }
    loadProject();
  }, [slug]);

  if (!project) {
    return (
      <div className="py-24 text-center text-stone-600 font-heading">
        Henter projekt...
      </div>
    );
  }

  const coverImg = Array.isArray(project.cover_image) ? project.cover_image[0] : project.cover_image;
  const galleryImages = project.gallery_images && project.gallery_images.length > 0
    ? project.gallery_images
    : [coverImg];

  return (
    <>
      <SEOHead 
        title={project.meta_title || `${project.title} - ${SITE_INFO.name}`}
        description={project.meta_description || project.description}
      />

      {/* Hero Header */}
      <section className="bg-stone-900 text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-4">
          <Link to="/projekter" className="inline-flex items-center gap-2 text-white hover:text-white/80 text-xs font-semibold uppercase tracking-wider transition-colors mb-2">
            <ArrowLeft className="w-4 h-4" />
            <span>Alle Projekter</span>
          </Link>
          
          <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase text-white opacity-90">
            <span>{project.location}</span>
            <span>•</span>
            <span>{project.year}</span>
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-5xl text-white tracking-tight max-w-4xl">
            {project.title}
          </h1>
        </div>
      </section>

      {/* Project Cover & Details */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 space-y-12">
          
          {/* Main Cover Image */}
          <div className="overflow-hidden border border-stone-200 shadow-2xl">
            <img
              src={`https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/${coverImg.replace('https://assets.auxodev.dk/', '')}`}
              alt={project.title}
              className="w-full max-h-[600px] object-cover"
            />
          </div>

          {/* Project Facts & Description */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-8 space-y-6">
              <h2 className="font-heading font-bold text-2xl text-stone-900 uppercase">
                Om projektet
              </h2>
              <p className="text-stone-700 text-base sm:text-lg leading-relaxed">
                {project.description}
              </p>
              <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                Udførelsen af dette projekt har fundet sted med særligt fokus på den faglige præcision, holdbarhed og æstetiske sammenhæng med den eksisterende arkitektur. Vores faglærte tømrere har sikret en smidig proces fra den første besigtigelse til det færdige resultat.
              </p>

              <div className="pt-4 border-t border-stone-200">
                <h3 className="font-heading font-bold text-lg text-stone-900 uppercase mb-4">
                  Højdepunkter & faglige leverancer
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-stone-700 text-sm sm:text-base">
                    <Check className="w-4 h-4 text-stone-900 mt-1 shrink-0" />
                    <span>Grundig forventningsafstemning, opmåling og faglig rådgivning før opstart.</span>
                  </li>
                  <li className="flex items-start gap-3 text-stone-700 text-sm sm:text-base">
                    <Check className="w-4 h-4 text-stone-900 mt-1 shrink-0" />
                    <span>Anvendelse af certificerede nordiske kvalitetsmaterialer med høj holdbarhed.</span>
                  </li>
                  <li className="flex items-start gap-3 text-stone-700 text-sm sm:text-base">
                    <Check className="w-4 h-4 text-stone-900 mt-1 shrink-0" />
                    <span>Skræddersyede snedkerløsninger og præcise samlinger udført efter stolte traditioner.</span>
                  </li>
                  <li className="flex items-start gap-3 text-stone-700 text-sm sm:text-base">
                    <Check className="w-4 h-4 text-stone-900 mt-1 shrink-0" />
                    <span>Løbende afstemning med kunden og overholdelse af den aftalte tidsramme.</span>
                  </li>
                  <li className="flex items-start gap-3 text-stone-700 text-sm sm:text-base">
                    <Check className="w-4 h-4 text-stone-900 mt-1 shrink-0" />
                    <span>Komplet oprydning og grundig slutfremvisning af det færdige håndværk.</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-4 bg-white p-6 border border-stone-200 shadow-sm space-y-4">
              <h3 className="font-heading font-bold text-stone-900 text-lg border-b border-stone-100 pb-3">Projektfakta</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-stone-400 text-xs block">Lokation</span>
                  <span className="font-semibold text-stone-800">{project.location}</span>
                </div>
                <div>
                  <span className="text-stone-400 text-xs block">Udført i år</span>
                  <span className="font-semibold text-stone-800">{project.year}</span>
                </div>
                <div>
                  <span className="text-stone-400 text-xs block">Hovedentreprenør</span>
                  <span className="font-semibold text-stone-800">{SITE_INFO.name}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Project Gallery Images */}
          <div className="space-y-6 pt-6">
            <h2 className="font-heading font-bold text-2xl text-stone-900">
              Billeder fra projektet
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {galleryImages.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setActiveImgIndex(idx);
                    setLightboxOpen(true);
                  }}
                  className="group relative h-64 overflow-hidden border border-stone-200 shadow-md cursor-pointer"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${img.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`${project.title} billede ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-stone-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white font-medium text-sm gap-2">
                    <ZoomIn className="w-5 h-5 text-amber-400" />
                    <span>Åbn i stor visning</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* Lightbox Modal */}
      <GalleryLightboxModal
        images={galleryImages}
        currentIndex={activeImgIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setActiveImgIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)}
        onNext={() => setActiveImgIndex((prev) => (prev + 1) % galleryImages.length)}
      />

      <ContactSection />
    </>
  );
}
